import React, {
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Skeleton,
  SxProps,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PopUp from "src/components/common/PopUp";
import { Button } from "src/components/common/FormComponents/Buttons";
import { fileUpload } from "src/utils/api/fileupload";
import { fundStatus } from "src/constants/common";
import { formatToIndianCurrency, getFinancialYear } from "src/utils/helper";

/**
 * Presentation kit for the Fund Application detail screen.
 *
 * The user and admin portals render the same application from two sides, so the
 * card / field / amount / timeline primitives live here and the file is kept
 * identical in `gcp-user-portal` and `gcp-admin-portal` — change both together.
 */

// The blank UC format the SNO fills, gets audited by a CA, and uploads back.
// Lives under `gcp-user-portal/public/` — only rendered where `mode ===
// "upload"`, which under the current portal behavior only the user portal's
// SNO ever reaches (the admin panel is view/approve-only), so the admin build
// does not need its own copy of the asset.
const UC_FORMAT_PDF_URL = `${process.env.PUBLIC_URL}/UC_Format.pdf`;

/**
 * Pinned to the top-right of the "Upload Utilization Certificate" panel's
 * header via `ActionPanel`'s `headerRight`, so the SNO can grab the blank
 * format and guidelines before filling the form below.
 */
export const DownloadUcFormatButton = () => (
  <Button
    text="Download UC Format and Guidelines"
    variant="outlined"
    onClick={() => window.open(UC_FORMAT_PDF_URL, "_blank", "noopener")}
    customStyles={{
      root: { fontSize: "12.5px", whiteSpace: "nowrap", flexShrink: 0 },
    }}
  />
);

export const fundUi = {
  deepGreen: "#0E5C4A",
  green: "#36B1A0",
  iconGreen: "#0E8A73",
  softGreen: "#EAF7F3",
  label: "#6B7A76",
  value: "#16241F",
  divider: "#EDF3F0",
  border: "#E4EFE9",
};

/**
 * Money for display. Amounts arrive from Sequelize as decimal strings and may
 * be null on older applications — both read as ₹0.00 rather than ₹NaN.
 */
export const money = (value?: number | string | null) =>
  formatToIndianCurrency(Number(value ?? 0));

/** 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 11 -> "11th". */
export const ordinal = (value: number) => {
  const lastDigit = value % 10;
  const lastTwoDigits = value % 100;
  if (lastDigit === 1 && lastTwoDigits !== 11) return `${value}st`;
  if (lastDigit === 2 && lastTwoDigits !== 12) return `${value}nd`;
  if (lastDigit === 3 && lastTwoDigits !== 13) return `${value}rd`;
  return `${value}th`;
};

/** "2025-2026" -> "2025-26", the short form this field is written in. */
const shortFinancialYear = (value: string) =>
  value.replace(/-(\d{2})(\d{2})$/, "-$2");

/**
 * "1st installment 2025-26" — which of an EPID's yearly fund applications this
 * one is, and the financial year it belongs to.
 *
 * `financialYear` is only stored on applications raised since that field was
 * added, so it falls back to the financial year of the application's own date
 * (the 1 April cut-off rule shared with the rest of both portals).
 */
export const installmentLabel = (fund?: {
  yearOfFundApplication?: number;
  financialYear?: string;
  applicationDate?: string;
  created_at?: string;
}) => {
  const derived = getFinancialYear(fund?.applicationDate ?? fund?.created_at);

  const financialYear = shortFinancialYear(
    fund?.financialYear?.trim() || (derived === "-" ? "" : derived)
  );

  return [
    fund?.yearOfFundApplication
      ? `${ordinal(Number(fund.yearOfFundApplication))} installment`
      : "",
    financialYear,
  ]
    .filter(Boolean)
    .join(" ");
};

const cardBase: SxProps = {
  backgroundColor: "#FFFFFF",
  border: `1px solid ${fundUi.border}`,
  borderRadius: "16px",
  boxShadow:
    "0px 1px 2px rgba(16, 40, 34, 0.04), 0px 10px 24px rgba(16, 40, 34, 0.04)",
  padding: "20px 22px",
  display: "flex",
  flexDirection: "column",
  "@media print": {
    boxShadow: "none",
    breakInside: "avoid",
  },
};

/** Responsive grid the detail cards are laid out in. */
export const DetailLayout = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "repeat(2, minmax(0, 1fr))",
        lg: "repeat(3, minmax(0, 1fr))",
      },
      gap: "20px",
      alignItems: "stretch",
      marginTop: "20px",
      "@media print": {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
    }}
  >
    {children}
  </Box>
);

type SectionCardProps = {
  title: string;
  icon?: ReactNode;
  /** Green left rail — marks the primary block of a row. */
  accent?: boolean;
  /** Span every column of the parent `DetailLayout`. */
  fullWidth?: boolean;
  headerRight?: ReactNode;
  children: ReactNode;
  sx?: SxProps;
};

export const SectionCard = ({
  title,
  icon,
  accent = false,
  fullWidth = false,
  headerRight,
  children,
  sx,
}: SectionCardProps) => (
  <Box
    sx={{
      ...(cardBase as object),
      ...(accent
        ? { borderLeft: `4px solid ${fundUi.green}`, paddingLeft: "19px" }
        : {}),
      ...(fullWidth ? { gridColumn: "1 / -1" } : {}),
      ...((sx as object) ?? {}),
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "18px",
      }}
    >
      {icon && (
        <Box
          sx={{
            width: "40px",
            height: "40px",
            flexShrink: 0,
            borderRadius: "11px",
            backgroundColor: fundUi.softGreen,
            color: fundUi.iconGreen,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: "22px" },
          }}
        >
          {icon}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          fontSize: "15.5px",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: fundUi.deepGreen,
          lineHeight: 1.35,
        }}
      >
        {title}
      </Box>
      {headerRight}
    </Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

/** Label above value — the read-only counterpart of a form field. */
export const DetailField = ({
  label,
  value,
  valueSx,
  span = false,
}: {
  label: string;
  value?: ReactNode;
  valueSx?: SxProps;
  /** Take the full width of the surrounding `DetailFieldGrid`. */
  span?: boolean;
}) => (
  <Box sx={span ? { gridColumn: "1 / -1" } : undefined}>
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        color: fundUi.label,
        lineHeight: 1.4,
        marginBottom: "4px",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        color: fundUi.value,
        lineHeight: 1.45,
        wordBreak: "break-word",
        ...((valueSx as object) ?? {}),
      }}
    >
      {value === 0 || !!value ? value : "-"}
    </Box>
  </Box>
);

export const DetailFieldGrid = ({
  columns = 2,
  children,
}: {
  columns?: number;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: "16px 20px",
    }}
  >
    {children}
  </Box>
);

type AmountTone = "default" | "total" | "soft" | "strong";

/**
 * A money line inside a fund card. `total` closes a list of components,
 * `soft` / `strong` highlight the released and balance figures.
 */
export const AmountRow = ({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  tone?: AmountTone;
}) => {
  const toneStyles: Record<AmountTone, SxProps> = {
    default: {
      padding: "7px 0",
    },
    total: {
      marginTop: "10px",
      paddingTop: "12px",
      borderTop: `1px dashed ${fundUi.border}`,
      color: fundUi.deepGreen,
      fontWeight: 600,
    },
    soft: {
      marginTop: "8px",
      padding: "10px 12px",
      borderRadius: "10px",
      backgroundColor: fundUi.softGreen,
      color: fundUi.deepGreen,
      fontWeight: 600,
    },
    strong: {
      marginTop: "8px",
      padding: "12px",
      borderRadius: "10px",
      backgroundColor: fundUi.deepGreen,
      color: "#FFFFFF",
      fontWeight: 700,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "12px",
        fontSize: "13px",
        color: fundUi.value,
        ...(toneStyles[tone] as object),
      }}
    >
      <Box sx={{ color: "inherit", opacity: tone === "default" ? 0.75 : 1 }}>
        {label}
      </Box>
      <Box
        sx={{
          fontWeight: tone === "default" ? 600 : "inherit",
          whiteSpace: "nowrap",
          textAlign: "right",
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

const STATUS_TONES: Record<string, "pending" | "query" | "success" | "danger"> =
  {
    CREATED: "pending",
    RAISED: "pending",
    UC_UPLOADED: "pending",
    NODAL_UNDER_QUERY: "query",
    UNDER_QUERY: "query",
    APPROVED: "success",
    PAYMENT_ACKNOWLEDGED: "success",
    UC_APPROVED: "success",
    REJECTED: "danger",
    UC_REJECTED: "danger",
  };

const TONE_COLORS = {
  pending: { bg: "#FFF4DE", color: "#9A6400" },
  query: { bg: "#E7F0FF", color: "#1B4F9C" },
  success: { bg: "#E3F7EC", color: "#137A4B" },
  danger: { bg: "#FDE8E8", color: "#B42318" },
};

/** The application's current status, as a chip next to its number. */
export const StatusPill = ({ status }: { status?: string }) => {
  if (!status) return null;

  const tone = TONE_COLORS[STATUS_TONES[status] ?? "pending"];

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 14px",
        borderRadius: "999px",
        backgroundColor: tone.bg,
        color: tone.color,
        fontSize: "13px",
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {fundStatus[status] ?? status}
    </Box>
  );
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const filePathOf = (url: string) => {
  try {
    return url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
  } catch {
    return url.split("?")[0];
  }
};

const fileNameOf = (url: string) => {
  const name = decodeURIComponent(filePathOf(url).split("/").pop() ?? "");
  return name || "Document";
};

const isImageFile = (url: string) =>
  IMAGE_EXTENSIONS.includes(
    filePathOf(url).split(".").pop()?.toLowerCase() ?? ""
  );

/**
 * An uploaded document, as a file row: thumbnail (images) or file glyph, the
 * file name, an optional upload date, and a download action. The whole tile
 * opens the file in a new tab.
 */
export const DocumentTile = ({
  label,
  fileUrl,
  meta,
}: {
  label?: string;
  fileUrl: string;
  meta?: string;
}) => {
  if (!fileUrl) return null;

  const name = fileNameOf(fileUrl);

  return (
    <Box sx={{ marginBottom: "12px", "&:last-of-type": { marginBottom: 0 } }}>
      {label && (
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: fundUi.label,
            marginBottom: "6px",
          }}
        >
          {label}
        </Box>
      )}
      <Box
        component="a"
        href={fileUrl}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "10px 12px",
          borderRadius: "12px",
          border: `1px solid ${fundUi.border}`,
          backgroundColor: "#FBFDFC",
          textDecoration: "none",
          color: "inherit",
          "&:hover": { borderColor: fundUi.green },
        }}
      >
        {isImageFile(fileUrl) ? (
          <Box
            component="img"
            src={fileUrl}
            alt={name}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              (e.target as HTMLImageElement).src =
                "/assets/images/NoImageFoundIcon.png";
            }}
            sx={{
              width: "44px",
              height: "44px",
              flexShrink: 0,
              objectFit: "cover",
              borderRadius: "8px",
              border: `1px solid ${fundUi.border}`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: "44px",
              height: "44px",
              flexShrink: 0,
              borderRadius: "8px",
              backgroundColor: "#FDECEC",
              color: "#C0392B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& svg": { fontSize: "22px" },
            }}
          >
            <InsertDriveFileOutlinedIcon />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            title={name}
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: fundUi.value,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Box>
          {meta && (
            <Box
              sx={{
                fontSize: "11.5px",
                color: fundUi.label,
                marginTop: "2px",
              }}
            >
              {meta}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            flexShrink: 0,
            fontSize: "12.5px",
            fontWeight: 600,
            color: fundUi.iconGreen,
            "& svg": { fontSize: "18px" },
            "@media print": { display: "none" },
          }}
        >
          <FileDownloadOutlinedIcon />
          Open
        </Box>
      </Box>
    </Box>
  );
};

export type StatusHistoryRow = {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  changedByRole: string;
  changedAt: string;
  remark?: string | null;
  user?: {
    id: string;
    firstName: string;
    lastName?: string;
  } | null;
};

// Transitions that send the application backwards (query / rejection) are
// marked so the IO <-> SNO <-> Admin to-and-fro reads at a glance.
const SEND_BACK_STATUSES = new Set([
  "UNDER_QUERY",
  "NODAL_UNDER_QUERY",
  "REJECTED",
  "UC_REJECTED",
]);

const ROLE_LABELS: Record<string, string> = {
  IMPLEMENTING_OFFICER: "Implementing Officer",
  NODAL_OFFICER: "SNO",
  ADMIN: "Admin",
  ENTITY: "Entity",
};

/** "20 May 2025" — the timeline reads as dates, not timestamps. */
export const shortDate = (value?: string | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

/** "20 May 2025, 10:26 AM" — used where the exact moment matters. */
const longDate = (value?: string | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

/** The fields of a fund application the milestone timeline is derived from. */
export type FundMilestoneSource = {
  status?: string;
  created_at?: string;
  applicationDate?: string;
  raisedDate?: string;
  releasedDate?: string;
  paymentAcknowledgementDate?: string;
  ucDate?: string;
};

type MilestoneState = "done" | "current" | "pending" | "failed";

/**
 * Where the application has reached in its lifecycle: a fixed set of stages,
 * each stamped with the date the backend recorded for it. This is deliberately
 * *not* the status history — queries and rejections bounce the application
 * between users without adding a stage, and those live in `StatusHistoryTrail`.
 */
export const ApplicationTimeline = ({
  fund,
}: {
  fund?: FundMilestoneSource;
}) => {
  const status = fund?.status;

  const stages = [
    {
      key: "created",
      label: "Submitted",
      date: fund?.applicationDate ?? fund?.created_at,
    },
    { key: "raised", label: "Forwarded to Admin", date: fund?.raisedDate },
    { key: "released", label: "Fund Released", date: fund?.releasedDate },
    {
      key: "receipt",
      label: "Payment Receipt Uploaded",
      date: fund?.paymentAcknowledgementDate,
    },
    { key: "uc", label: "Utilization Certificate", date: fund?.ucDate },
    {
      key: "ucApproved",
      label: "UC Accepted",
      date: undefined,
      reached: status === "UC_APPROVED",
    },
  ];

  const reachedFlags = stages.map(
    (stage) => !!stage.date || !!(stage as { reached?: boolean }).reached
  );
  const lastReached = reachedFlags.lastIndexOf(true);

  const nodes: {
    key: string;
    label: string;
    date?: string;
    state: MilestoneState;
  }[] = stages.map((stage, index) => ({
    key: stage.key,
    label: stage.label,
    date: stage.date,
    state:
      index < lastReached
        ? "done"
        : index === lastReached
        ? "current"
        : "pending",
  }));

  // A rejected application stops where it stopped — the stages it never got to
  // are dropped rather than left hanging as "pending".
  const isRejected = status === "REJECTED" || status === "UC_REJECTED";

  const visibleNodes = isRejected
    ? [
        ...nodes.slice(0, lastReached + 1).map((node) => ({
          ...node,
          state: "done" as MilestoneState,
        })),
        {
          key: "rejected",
          label: fundStatus[status] ?? "Rejected",
          date: undefined,
          state: "failed" as MilestoneState,
        },
      ]
    : nodes;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        overflowX: "auto",
        paddingBottom: "6px",
        "@media print": { flexWrap: "wrap", overflowX: "visible" },
      }}
    >
      {visibleNodes.map((node, index) => {
        const isLast = index === visibleNodes.length - 1;
        const accent =
          node.state === "failed"
            ? "#B42318"
            : node.state === "pending"
            ? "#C4D8D1"
            : fundUi.iconGreen;

        return (
          <Fragment key={node.key}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                flexShrink: 0,
                maxWidth: "200px",
              }}
            >
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${accent}`,
                  backgroundColor:
                    node.state === "current" || node.state === "pending"
                      ? "#FFFFFF"
                      : accent,
                  color: "#FFFFFF",
                  "& svg": { fontSize: "15px" },
                }}
              >
                {node.state === "current" && (
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: accent,
                    }}
                  />
                )}
                {node.state === "done" && <CheckRoundedIcon />}
                {node.state === "failed" && <PriorityHighRoundedIcon />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: "13px",
                    fontWeight: node.state === "pending" ? 500 : 600,
                    color:
                      node.state === "failed"
                        ? accent
                        : node.state === "pending"
                        ? fundUi.label
                        : fundUi.value,
                    lineHeight: 1.35,
                  }}
                >
                  {node.label}
                </Box>
                <Box
                  sx={{
                    fontSize: "11.5px",
                    color: fundUi.label,
                    marginTop: "2px",
                  }}
                >
                  {node.state === "pending"
                    ? "Pending"
                    : shortDate(node.date) || "—"}
                </Box>
              </Box>
            </Box>
            {!isLast && (
              <Box
                sx={{
                  flex: "1 1 24px",
                  minWidth: "24px",
                  marginTop: "11px",
                  borderTop: `1px dashed ${
                    visibleNodes[index + 1].state === "pending"
                      ? "#DDE7E3"
                      : "#9FCFC4"
                  }`,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

/**
 * The append-only audit trail: every recorded status change with who made it,
 * when, and why. Separate from `ApplicationTimeline` — this is the full
 * to-and-fro, including queries and rejections that do not advance a stage.
 */
export const StatusHistoryTrail = ({
  statusHistory,
  collapsedCount = 1,
}: {
  statusHistory?: StatusHistoryRow[];
  /** How many of the most recent updates stay visible when collapsed. */
  collapsedCount?: number;
}) => {
  const [expanded, setExpanded] = useState(false);

  const rows = statusHistory ?? [];

  if (!rows.length)
    return (
      <Box sx={{ fontSize: "12.5px", color: fundUi.label }}>
        No status changes have been recorded against this application.
      </Box>
    );

  const hasMore = rows.length > collapsedCount;

  // Order stays oldest-first to match the stages above; it is the *earlier*
  // updates that fold away, so a collapsed trail ends on the latest one.
  const foldedRows = hasMore ? rows.slice(0, rows.length - collapsedCount) : [];
  const openRows = hasMore ? rows.slice(rows.length - collapsedCount) : rows;

  const renderRow = (row: StatusHistoryRow, index: number) => {
    const isSendBack = SEND_BACK_STATUSES.has(row.toStatus);
    const accent = isSendBack ? "#B42318" : fundUi.iconGreen;
    const isLast = index === rows.length - 1;
    const actorName = row.user
      ? `${row.user.firstName ?? ""} ${row.user.lastName ?? ""}`.trim()
      : "";

    return (
      <Box
        key={row.id}
        sx={{
          position: "relative",
          paddingLeft: "22px",
          paddingBottom: isLast ? 0 : "18px",
          borderLeft: `1px solid ${isLast ? "transparent" : "#DDE7E3"}`,
          marginLeft: "5px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "-5px",
            top: "3px",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: accent,
          }}
        />
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: isSendBack ? accent : fundUi.value,
            lineHeight: 1.35,
          }}
        >
          {row.fromStatus
            ? `${fundStatus[row.fromStatus] ?? row.fromStatus} → ${
                fundStatus[row.toStatus] ?? row.toStatus
              }`
            : fundStatus[row.toStatus] ?? row.toStatus}
        </Box>
        <Box
          sx={{
            fontSize: "11.5px",
            color: fundUi.label,
            marginTop: "3px",
          }}
        >
          {[
            actorName,
            ROLE_LABELS[row.changedByRole] ?? row.changedByRole,
            longDate(row.changedAt),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Box>
        {row.remark && (
          <Box
            sx={{
              fontSize: "12.5px",
              color: fundUi.value,
              marginTop: "6px",
              padding: "8px 10px",
              borderRadius: "8px",
              backgroundColor: "#F7FAF9",
              whiteSpace: "pre-wrap",
            }}
          >
            {row.remark}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Folded rows stay mounted so a print always carries the full trail. */}
      {!!foldedRows.length && (
        <Box
          sx={{
            display: expanded ? "block" : "none",
            "@media print": { display: "block" },
          }}
        >
          {foldedRows.map((row, index) => renderRow(row, index))}
        </Box>
      )}
      {openRows.map((row, index) => renderRow(row, foldedRows.length + index))}
      {hasMore && (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((value) => !value)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            marginTop: "16px",
            padding: "6px 12px",
            border: `1px solid ${"#C4D8D1"}`,
            borderRadius: "999px",
            backgroundColor: "#FFFFFF",
            color: fundUi.iconGreen,
            fontFamily: "Poppins",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": {
              borderColor: fundUi.green,
              backgroundColor: fundUi.softGreen,
            },
            "& svg": {
              fontSize: "18px",
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "none",
            },
            "@media print": { display: "none" },
          }}
        >
          {expanded
            ? "Show recent only"
            : `Show all ${rows.length} status updates`}
          <KeyboardArrowDownRoundedIcon />
        </Box>
      )}
    </Box>
  );
};

/** A labelled block inside a card — used to keep two related lists distinct. */
export const SubSection = ({
  title,
  align = "left",
  divider = true,
  children,
}: {
  title: string;
  align?: "left" | "right";
  /** Off when the block sits inside a `SubSectionRow`, which rules its own. */
  divider?: boolean;
  children: ReactNode;
}) => (
  <Box
    sx={{
      ...(divider
        ? {
            marginTop: "24px",
            paddingTop: "20px",
            borderTop: `1px solid ${fundUi.divider}`,
          }
        : {}),
      ...(align === "right" ? { textAlign: { xs: "left", md: "right" } } : {}),
    }}
  >
    <Box
      sx={{
        fontSize: "13.5px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: fundUi.deepGreen,
        marginBottom: "16px",
      }}
    >
      {title}
    </Box>
    {children}
  </Box>
);

/**
 * Two `SubSection`s side by side under one rule — the wide list on the left,
 * a narrow block of figures on the right. Stacks on small screens.
 */
export const SubSectionRow = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
      gap: "24px 56px",
      marginTop: "24px",
      paddingTop: "20px",
      borderTop: `1px solid ${fundUi.divider}`,
    }}
  >
    {children}
  </Box>
);

/**
 * Anchors for the action panels on the fund application detail page. The list
 * pages append one as `?section=` so an Action click lands on the exact panel
 * that action belongs to, rather than at the top of a long page.
 */
export const FUND_SECTIONS = {
  /** SNO: upload the signed FA / reply to a query and forward it on. */
  forward: "fa-forward",
  /** SNO: upload the payment receipt once the fund is released. */
  receipt: "fa-receipt",
  /** SNO or admin: the utilization certificate form. */
  uc: "fa-uc",
  /** Admin: enter the released amount and approve. */
  release: "fa-release",
  /** Admin: accept the uploaded utilization certificate. */
  verifyUc: "fa-verify-uc",
};

/**
 * Scrolls to the `?section=` panel once the page has data to render. Waits a
 * frame so the panel exists and the cards above it have taken their height,
 * otherwise the browser scrolls to where the panel *was*.
 */
export const useScrollToSection = (
  sectionId: string | null,
  ready: boolean
) => {
  useEffect(() => {
    if (!sectionId || !ready) return;

    const timer = setTimeout(() => {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);

    return () => clearTimeout(timer);
  }, [sectionId, ready]);
};

/**
 * Card that holds the actions available to this user on this application. The
 * green top rail separates "what you can do" from the read-only cards above.
 */
export const ActionPanel = ({
  id,
  title,
  description,
  icon,
  highlighted = false,
  headerRight,
  busy = false,
  blocked,
  children,
}: {
  /** Anchor for `FUND_SECTIONS` deep links from the fund application list. */
  id?: string;
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Ringed when the page was opened specifically to reach this panel. */
  highlighted?: boolean;
  /** Pinned to the top-right of the header, alongside the title. */
  headerRight?: ReactNode;
  /**
   * Covers the panel while its action is still running, so the form cannot be
   * touched again mid-flight and the wait is visible on the panel itself and
   * not only inside the confirmation dialog.
   */
  busy?: boolean;
  /**
   * Covers the panel with guidance instead of letting it be used, for a step
   * that is not wrong but is simply not this one's turn yet. The content stays
   * readable underneath; only acting on it is withheld.
   */
  blocked?: {
    title: string;
    message: string;
    /** Usually a button that takes the SNO to the step that comes first. */
    action?: ReactNode;
  };
  children: ReactNode;
}) => (
  <Box
    id={id}
    sx={{
      ...(cardBase as object),
      position: "relative",
      marginTop: "20px",
      padding: { xs: "20px 18px", md: "24px 26px" },
      borderColor: highlighted ? fundUi.green : "#CDE9E1",
      borderTop: `3px solid ${fundUi.green}`,
      backgroundColor: "#FFFFFF",
      // Keeps the panel clear of the sticky header once scrolled to.
      scrollMarginTop: "24px",
      ...(highlighted
        ? { boxShadow: `0 0 0 3px rgba(54, 177, 160, 0.22)` }
        : {}),
      "@media print": { display: "none" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: "22px",
      }}
    >
      {icon && (
        <Box
          sx={{
            width: "40px",
            height: "40px",
            flexShrink: 0,
            borderRadius: "11px",
            backgroundColor: fundUi.softGreen,
            color: fundUi.iconGreen,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: "22px" },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            fontSize: "15.5px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: fundUi.deepGreen,
            lineHeight: 1.35,
          }}
        >
          {title}
        </Box>
        {description && (
          <Box
            sx={{
              fontSize: "13px",
              color: fundUi.label,
              marginTop: "5px",
              lineHeight: 1.5,
            }}
          >
            {description}
          </Box>
        )}
      </Box>
      {headerRight}
    </Box>
    {/* Left mounted and readable, but inert while the overlay is up. */}
    <Box
      sx={blocked ? { pointerEvents: "none", userSelect: "none" } : undefined}
    >
      {children}
    </Box>
    {!busy && blocked && (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "16px",
          backgroundColor: "rgba(255, 255, 255, 0.88)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: "440px",
            textAlign: "center",
            padding: "22px 24px",
            borderRadius: "14px",
            border: `1px solid ${fundUi.border}`,
            backgroundColor: "#FFFFFF",
            boxShadow: "0 10px 30px rgba(14, 92, 74, 0.12)",
          }}
        >
          <Box
            sx={{
              width: "44px",
              height: "44px",
              margin: "0 auto 12px",
              borderRadius: "13px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: TONE_COLORS.pending.bg,
              color: TONE_COLORS.pending.color,
              "& svg": { fontSize: "24px" },
            }}
          >
            <LockOutlinedIcon />
          </Box>
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: fundUi.value,
              marginBottom: "6px",
            }}
          >
            {blocked.title}
          </Box>
          <Box sx={{ fontSize: "13px", color: fundUi.label, lineHeight: 1.6 }}>
            {blocked.message}
          </Box>
          {blocked.action && (
            <Box sx={{ marginTop: "16px" }}>{blocked.action}</Box>
          )}
        </Box>
      </Box>
    )}
    {busy && (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "16px",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          zIndex: 2,
        }}
      >
        <CircularProgress size="2rem" />
        <Box
          sx={{ fontSize: "13px", fontWeight: 600, color: fundUi.deepGreen }}
        >
          Submitting…
        </Box>
      </Box>
    )}
  </Box>
);

/**
 * Field layout inside an `ActionPanel` — inputs share the column width of the
 * grid rather than each carrying its own percentage.
 */
export const FormGrid = ({
  columns = 2,
  children,
}: {
  columns?: number;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        lg: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: "22px 24px",
      alignItems: "start",
    }}
  >
    {children}
  </Box>
);

/** One labelled block of an `ActionPanel` — takes the whole grid row. */
export const FormRow = ({ children }: { children: ReactNode }) => (
  <Box sx={{ gridColumn: "1 / -1" }}>{children}</Box>
);

/**
 * A supporting action inside a form — a short explanation with its button,
 * boxed so it doesn't read as one of the fields.
 */
export const FormHint = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      padding: "14px 16px",
      borderRadius: "12px",
      border: `1px dashed ${"#C9DBD4"}`,
      backgroundColor: "#FBFDFC",
    }}
  >
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 600,
        color: fundUi.value,
      }}
    >
      {label}
    </Box>
    {description && (
      <Box
        sx={{
          fontSize: "12px",
          color: fundUi.label,
          marginTop: "4px",
          lineHeight: 1.5,
        }}
      >
        {description}
      </Box>
    )}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginTop: "12px",
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * Footer for the buttons of an `ActionPanel`. `leftAction` holds the way out of
 * the decision (raising a query); `children` hold the decisions themselves, so
 * the two never sit side by side.
 */
export const ActionFooter = ({
  leftAction,
  children,
}: {
  leftAction?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "16px",
      marginTop: "28px",
      paddingTop: "22px",
      borderTop: `1px solid ${fundUi.divider}`,
    }}
  >
    {leftAction}
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "16px",
        marginLeft: "auto",
      }}
    >
      {children}
    </Box>
  </Box>
);

/** Dashed drop zone with a hidden file input, in the card system's styling. */
const UploadBox = ({
  title,
  inputId,
  file,
  existingUrl,
  onPick,
}: {
  title: string;
  inputId: string;
  file: File | null;
  existingUrl?: string;
  onPick: (file: File | null) => void;
}) => (
  <Box>
    {existingUrl && (
      <Box sx={{ marginBottom: "10px" }}>
        <DocumentTile label="Currently uploaded" fileUrl={existingUrl} />
      </Box>
    )}
    <Box
      sx={{
        border: `1px dashed ${fundUi.green}`,
        borderRadius: "12px",
        padding: "22px 16px",
        textAlign: "center",
        backgroundColor: "#F7FCFB",
      }}
    >
      <CloudUploadOutlinedIcon
        sx={{ fontSize: "28px", color: fundUi.iconGreen }}
      />
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: fundUi.value,
          margin: "6px 0 12px",
        }}
      >
        {title}
      </Box>
      <Box
        component="input"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        type="file"
        id={inputId}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onPick(event.target.files?.[0] ?? null)
        }
        sx={{ display: "none" }}
      />
      <Box component="label" htmlFor={inputId}>
        <Box
          component="span"
          sx={{
            display: "inline-block",
            padding: "8px 18px",
            borderRadius: "8px",
            backgroundColor: fundUi.green,
            color: "#FFFFFF",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { backgroundColor: fundUi.iconGreen },
          }}
        >
          Browse Files
        </Box>
      </Box>
      {file && (
        <Box
          sx={{
            marginTop: "12px",
            fontSize: "12.5px",
            fontWeight: 600,
            color: fundUi.iconGreen,
            wordBreak: "break-all",
          }}
        >
          ✓ {file.name}
        </Box>
      )}
    </Box>
  </Box>
);

const ucInputSx = {
  width: "100%",
  height: "40px",
  padding: "10px 12px",
  borderRadius: "10px",
  border: `1px solid ${fundUi.border}`,
  backgroundColor: "#FFFFFF",
  fontFamily: "Poppins",
  fontSize: "13px",
  color: fundUi.value,
  outline: "none",
  "&:focus": { borderColor: fundUi.green },
};

/** `ucInputSx` for free text: grows with the remark instead of clipping it. */
const ucTextareaSx = {
  ...ucInputSx,
  height: "auto",
  minHeight: "92px",
  lineHeight: 1.5,
  resize: "vertical" as const,
};

/** Label above a bare input, matching `DetailField`'s label. */
const UcFieldLabel = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      fontSize: "12px",
      fontWeight: 500,
      color: fundUi.label,
      marginBottom: "6px",
    }}
  >
    {children}
  </Box>
);

export type UcSubmitPayload = {
  utilizationCertificateDocId?: string;
  utilizedAmount: number;
  ucDate?: string;
  caName?: string;
  udinNumber?: string;
  /**
   * The SNO's note to the admin, sent when re-uploading a rejected
   * certificate. The endpoint records it against the status change and stores
   * it as the application's SNO remark.
   */
  remark?: string;
  revalidationLetterDocId?: string;
};

/**
 * The Upload Utilization Certificate form, inline on the detail page.
 *
 * Figures come from aggregates the fund-application endpoint already returns.
 * The two files are uploaded to storage here and the resulting keys handed to
 * the page, which owns the status change — the portals patch to different
 * statuses (the SNO uploads for review, the admin records an accepted UC).
 */
export const UtilizationCertificatePanel = ({
  data,
  ecoRestorationCost,
  mode,
  isSubmitting = false,
  onSubmit,
  enforcePreviousUcGate = true,
  blockedReason,
}: {
  data: any;
  ecoRestorationCost?: number | string;
  mode: "upload" | "view";
  isSubmitting?: boolean;
  onSubmit?: (payload: UcSubmitPayload) => void;
  /**
   * Blocks this panel while an earlier FA's UC is still unapproved. On by
   * default for the SNO's own upload (user portal). The admin is the
   * approver, not a restricted party, so the admin portal opts out — an
   * admin recording/approving a UC is never something to gate.
   */
  enforcePreviousUcGate?: boolean;
  /**
   * Set while an earlier step is still outstanding (the payment receipt). The
   * page covers this panel with an overlay, so this is the keyboard-level
   * backstop rather than the visible restriction.
   */
  blockedReason?: string;
}) => {
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [revalidationFile, setRevalidationFile] = useState<File | null>(null);
  const [utilizedAmount, setUtilizedAmount] = useState("");
  const [ucDate, setUcDate] = useState("");
  const [caName, setCaName] = useState("");
  const [udinNumber, setUdinNumber] = useState("");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState("");
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const fund = data?.fundDetails;

  const isView = mode === "view";

  // The admin sent the certificate back, so this upload is a correction. The
  // remark field is offered on every upload; this only decides whether the
  // panel also shows the reason the certificate was rejected.
  const isReupload = !isView && fund?.status === "UC_REJECTED";

  // The reason is read off the transition that recorded it. `remark` on the
  // application holds the same text but is overwritten by whatever remark
  // comes next, so it is only the fallback for applications whose rejection
  // predates the status history.
  const rejectionRemark = useMemo(() => {
    const rows: StatusHistoryRow[] = data?.statusHistory ?? [];

    // Oldest-first, so the last match is the most recent rejection.
    const latest = [...rows]
      .reverse()
      .find((row) => row.toStatus === "UC_REJECTED");

    return latest?.remark || fund?.remark || "";
  }, [data?.statusHistory, fund?.remark]);

  const totalPaymentReleased = Number(data?.totalPaymentReleasedTillDate ?? 0);
  const previousFaPayment = Number(data?.previousFaPaymentReleased ?? 0);
  const financialProgress = Number(data?.totalFinancialProgress ?? 0);
  const storedUtilized = Number(fund?.utilizedAmount ?? 0);

  const utilized = isView ? storedUtilized : parseFloat(utilizedAmount) || 0;

  // What the previous FA's certificate left unspent. It rolls forward, so this
  // FA's UC has to account for it on top of this FA's own release — FA3 covers
  // its release plus whatever FA2 did not use, and so on. 0 for FA1.
  const carriedForward = Number(data?.previousFaUnutilizedAmount ?? 0);
  const hasPreviousFa = Number(fund?.yearOfFundApplication ?? 0) > 1;

  // Amount this certificate has to account for, and what it leaves behind:
  //   balance = (released against this FA + carried in) - utilized
  // That balance is stored against the FA and becomes the next one's opening
  // figure. Where the backend has recorded it, that stored value is the
  // authority; the computed one drives the live form and still covers rows
  // saved before the figure was stored.
  const openingBalance = previousFaPayment + carriedForward;
  const computedBalance = openingBalance - utilized;
  const balanceForRevalidation =
    isView && fund?.unutilizedAmount != null
      ? Number(fund.unutilizedAmount)
      : computedBalance;
  const needsRevalidationLetter = balanceForRevalidation > 0;

  const existingUcUrl = data?.utilizationCertificateDocUrl;
  const existingRvUrl = data?.revalidationLetterDocUrl;
  const existingUcKey = fund?.utilizationCertificateDocId;
  const existingRvKey = fund?.revalidationLetterDocId;

  // A letter is asked for whenever something is left to revalidate. Reading a
  // certificate back, one already filed is shown whatever the balance says — a
  // UC that spent its release in full still has its letter on record, and
  // hiding it would drop a filed document off the page.
  const showRevalidationLetter =
    needsRevalidationLetter || (isView && !!existingRvUrl);

  // Earlier FA years (of this EPID) whose own UC has not been approved yet.
  // Non-empty only while `mode === "upload"` is even relevant — a viewed UC
  // was, by definition, already allowed to be uploaded.
  const pendingPreviousUcYears: number[] = enforcePreviousUcGate
    ? data?.pendingPreviousUcYears ?? []
    : [];
  const isBlockedByPreviousUc = !isView && pendingPreviousUcYears.length > 0;
  const pendingPreviousUcLabel = pendingPreviousUcYears
    .map((year) => `FA${year}`)
    .join(", ");
  const previousUcBlockedMessage = `Upload the Utilization Certificate for ${pendingPreviousUcLabel} and get it approved by the admin before you can upload the UC for this Fund Application.`;

  // Grabs attention the moment the SNO reaches a panel they cannot act on yet
  // — re-armed whenever a different (still-blocked) FA is opened.
  const [showPreviousUcPopup, setShowPreviousUcPopup] = useState(
    isBlockedByPreviousUc
  );
  useEffect(() => {
    if (isBlockedByPreviousUc) setShowPreviousUcPopup(true);
  }, [fund?.id, isBlockedByPreviousUc]);

  // Pre-fill from whatever is already saved (e.g. a rejected UC being redone)
  // so the figures need not be re-entered from scratch.
  useEffect(() => {
    if (!fund) return;
    setUtilizedAmount(
      (previous) =>
        previous ||
        (fund.utilizedAmount != null ? String(fund.utilizedAmount) : "")
    );
    setUcDate(
      (previous) =>
        previous || (fund.ucDate ? String(fund.ucDate).slice(0, 10) : "")
    );
    setCaName((previous) => previous || fund.caName || "");
    setUdinNumber((previous) => previous || fund.udinNumber || "");
  }, [fund]);

  const handleSubmit = async () => {
    // Reachable by keyboard even under the page's overlay, so it is re-checked
    // here rather than left to the covering layer.
    if (blockedReason) {
      setError(blockedReason);
      return;
    }

    // Defence in depth — the form is hidden while blocked, but the backend is
    // the source of truth, so re-check rather than trust stale client state.
    if (isBlockedByPreviousUc) {
      setShowPreviousUcPopup(true);
      return;
    }

    if (!certificateFile && !existingUcKey) {
      setError("Please select a Utilization Certificate file to upload.");
      return;
    }

    if (utilized > openingBalance) {
      setError(
        `Utilized amount cannot exceed the available balance of ${money(
          openingBalance
        )}.`
      );
      return;
    }

    if (needsRevalidationLetter && !revalidationFile && !existingRvKey) {
      setError("Please select a Revalidation Letter file to upload.");
      return;
    }

    setIsUploadingFiles(true);
    setError("");
    try {
      let ucKey: string | undefined = existingUcKey;
      if (certificateFile) {
        const ucFormData = new FormData();
        ucFormData.append("file", certificateFile);
        const ucResponse = await fileUpload(ucFormData as any);
        ucKey = ucResponse?.data?.data?.uploadedFile?.key;
        if (!ucKey) {
          setError("Failed to upload certificate. Please try again.");
          return;
        }
      }

      let revalidationKey: string | undefined = existingRvKey;
      if (revalidationFile) {
        const rvFormData = new FormData();
        rvFormData.append("file", revalidationFile);
        const rvResponse = await fileUpload(rvFormData as any);
        revalidationKey = rvResponse?.data?.data?.uploadedFile?.key;
      }

      onSubmit?.({
        utilizationCertificateDocId: ucKey,
        utilizedAmount: utilized,
        ...(ucDate ? { ucDate } : {}),
        ...(caName ? { caName } : {}),
        ...(udinNumber ? { udinNumber } : {}),
        ...(remark.trim() ? { remark: remark.trim() } : {}),
        ...(revalidationKey
          ? { revalidationLetterDocId: revalidationKey }
          : {}),
      });
    } catch {
      setError("Failed to upload file. Please try again.");
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const busy = isUploadingFiles || isSubmitting;

  return (
    <Box>
      {isBlockedByPreviousUc && (
        <PopUp
          open={showPreviousUcPopup}
          handleClose={() => setShowPreviousUcPopup(false)}
          heading=""
          actionLeft={{ hidden: true }}
          actionRight={{
            label: "Okay",
            onClick: () => setShowPreviousUcPopup(false),
          }}
        >
          <Box sx={{ maxWidth: "420px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "14px",
              }}
            >
              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: TONE_COLORS.danger.bg,
                  color: TONE_COLORS.danger.color,
                  "& svg": { fontSize: "22px" },
                }}
              >
                <PriorityHighRoundedIcon />
              </Box>
              <Box
                sx={{
                  fontSize: "17px",
                  fontWeight: 600,
                  color: fundUi.value,
                  lineHeight: 1.3,
                }}
              >
                Previous Fund Application pending
              </Box>
            </Box>
            <Box
              sx={{ fontSize: "14px", color: fundUi.label, lineHeight: 1.6 }}
            >
              {previousUcBlockedMessage}
            </Box>
          </Box>
        </PopUp>
      )}

      {isBlockedByPreviousUc ? (
        <Box
          sx={{
            padding: "14px 16px",
            borderRadius: "12px",
            backgroundColor: TONE_COLORS.danger.bg,
            color: TONE_COLORS.danger.color,
            fontSize: "13px",
            lineHeight: 1.6,
          }}
        >
          <Box sx={{ fontWeight: 600, marginBottom: "4px" }}>
            Previous Fund Application pending
          </Box>
          {previousUcBlockedMessage}
        </Box>
      ) : (
        <>
          {error && (
            <Box
              sx={{
                marginBottom: "18px",
                padding: "10px 14px",
                borderRadius: "10px",
                backgroundColor: TONE_COLORS.danger.bg,
                color: TONE_COLORS.danger.color,
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {error}
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: "0 24px",
              alignItems: "start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              <DetailField
                label="Total Eco-restoration Cost (INR) (as per cost estimate)"
                value={money(ecoRestorationCost)}
              />
              <DetailField
                label="Total Payment Released till date (INR)"
                value={money(totalPaymentReleased)}
              />
              <DetailField
                label="Payment Released in previous Fund application (INR)"
                value={money(previousFaPayment)}
              />
              {hasPreviousFa && (
                <DetailField
                  label="Unutilized Amount carried forward from previous FA (INR)"
                  value={money(carriedForward)}
                />
              )}
              <DetailField
                label="Total Available Balance (INR) (Payment Released + Carried Forward)"
                value={money(openingBalance)}
              />
              <DetailField
                label="Balance Amount (INR) for revalidation"
                value={money(balanceForRevalidation)}
                valueSx={{ color: fundUi.deepGreen }}
              />
              <DetailField
                label="Financial Progress as per Portal (INR)"
                value={money(financialProgress)}
              />
              <Box>
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: fundUi.value,
                    marginBottom: "8px",
                  }}
                >
                  Utilization Certificate (CA Audited PDF duly signed by CA and
                  SNO)
                </Box>
                {isView ? (
                  existingUcUrl ? (
                    <DocumentTile fileUrl={existingUcUrl} />
                  ) : (
                    <Box sx={{ fontSize: "12.5px", color: fundUi.label }}>
                      No document available
                    </Box>
                  )
                ) : (
                  <UploadBox
                    title="Upload Utilization Certificate"
                    inputId="uc-file-upload"
                    file={certificateFile}
                    existingUrl={existingUcUrl}
                    onPick={(picked) => {
                      setCertificateFile(picked);
                      setError("");
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              {isView ? (
                <DetailField
                  label="Utilized Amount (INR as per audited UC)"
                  value={money(storedUtilized)}
                />
              ) : (
                <Box>
                  <UcFieldLabel>
                    Utilized Amount (INR as per audited UC)
                  </UcFieldLabel>
                  <Box
                    component="input"
                    type="number"
                    value={utilizedAmount}
                    placeholder="Enter amount"
                    max={openingBalance}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const nextValue = event.target.value;
                      const parsed = parseFloat(nextValue);
                      setUtilizedAmount(
                        nextValue !== "" &&
                          !Number.isNaN(parsed) &&
                          parsed > openingBalance
                          ? String(openingBalance)
                          : nextValue
                      );
                    }}
                    sx={ucInputSx}
                  />
                  <Box
                    sx={{
                      fontSize: "11px",
                      color: fundUi.label,
                      marginTop: "4px",
                    }}
                  >
                    Cannot exceed the available balance of{" "}
                    {money(openingBalance)}.
                  </Box>
                </Box>
              )}
              {isView ? (
                <DetailField
                  label="UC Date"
                  value={fund?.ucDate ? shortDate(fund.ucDate) : ""}
                />
              ) : (
                <Box>
                  <UcFieldLabel>UC Date</UcFieldLabel>
                  <Box
                    component="input"
                    type="date"
                    value={ucDate}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setUcDate(event.target.value)
                    }
                    sx={ucInputSx}
                  />
                </Box>
              )}
              {isView ? (
                <DetailField label="CA Name" value={fund?.caName} />
              ) : (
                <Box>
                  <UcFieldLabel>CA Name</UcFieldLabel>
                  <Box
                    component="input"
                    type="text"
                    value={caName}
                    placeholder="Name of the auditing CA"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setCaName(event.target.value)
                    }
                    sx={ucInputSx}
                  />
                </Box>
              )}
              {isView ? (
                <DetailField label="UDIN Number" value={fund?.udinNumber} />
              ) : (
                <Box>
                  <UcFieldLabel>UDIN Number</UcFieldLabel>
                  <Box
                    component="input"
                    type="text"
                    value={udinNumber}
                    placeholder="Unique Document Identification Number"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setUdinNumber(event.target.value)
                    }
                    sx={ucInputSx}
                  />
                </Box>
              )}
              {showRevalidationLetter && (
                <Box>
                  <Box
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: fundUi.value,
                      marginBottom: "8px",
                    }}
                  >
                    Revalidation Letter (signed by IO/DNO and SNO)
                  </Box>
                  {isView ? (
                    existingRvUrl ? (
                      <DocumentTile fileUrl={existingRvUrl} />
                    ) : (
                      <Box sx={{ fontSize: "12.5px", color: fundUi.label }}>
                        No document available
                      </Box>
                    )
                  ) : (
                    <UploadBox
                      title="Upload Revalidation Letter"
                      inputId="rv-file-upload"
                      file={revalidationFile}
                      existingUrl={existingRvUrl}
                      onPick={setRevalidationFile}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {!isView && (
            <Box sx={{ marginTop: "24px" }}>
              {isReupload && rejectionRemark && (
                <Box
                  sx={{
                    marginBottom: "18px",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    border: "1px solid #F4C8C2",
                    backgroundColor: "#FDF4F3",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#B42318",
                      marginBottom: "6px",
                    }}
                  >
                    Why the admin sent this back
                  </Box>
                  <Box
                    sx={{
                      fontSize: "12.5px",
                      color: fundUi.value,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {rejectionRemark}
                  </Box>
                </Box>
              )}
              <UcFieldLabel>Your Remark / Query (optional)</UcFieldLabel>
              <Box
                component="textarea"
                value={remark}
                maxLength={1000}
                placeholder={
                  isReupload
                    ? "Tell the admin what you have corrected in this certificate, or raise a query about the rejection."
                    : "Add a remark for the admin, or raise a query about this certificate."
                }
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setRemark(event.target.value)
                }
                sx={ucTextareaSx}
              />
              <Box
                sx={{ fontSize: "11px", color: fundUi.label, marginTop: "4px" }}
              >
                Sent to the admin with the certificate and recorded in this
                application's status history.
              </Box>
            </Box>
          )}

          {!isView && (
            <ActionFooter>
              <Button
                text={isUploadingFiles ? "Uploading..." : "Submit"}
                loading={busy}
                disabled={
                  busy ||
                  (!certificateFile && !existingUcKey) ||
                  (needsRevalidationLetter &&
                    !revalidationFile &&
                    !existingRvKey)
                }
                onClick={handleSubmit}
                customStyles={{ root: { fontSize: "16px" } }}
              />
            </ActionFooter>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Statuses from the payment receipt onwards — the point where this FA's own
 * utilization certificate becomes the subject and the previous year's stops
 * being the thing to look at.
 */
const UC_STAGE_STATUSES = new Set([
  "PAYMENT_ACKNOWLEDGED",
  "UC_UPLOADED",
  "UC_APPROVED",
  "UC_REJECTED",
]);

export const hasReachedUcStage = (status?: string) =>
  !!status && UC_STAGE_STATUSES.has(status);

/**
 * Statuses an application only reaches once the admin has released the fund —
 * the point from which its payment receipt can be recorded. The receipt is
 * normally uploaded straight away, but an application can travel on to the UC
 * stage without one, so these are all the statuses where it may still be filed.
 */
const FUND_RELEASED_STATUSES = new Set([
  "APPROVED",
  "PAYMENT_ACKNOWLEDGED",
  "UC_UPLOADED",
  "UC_APPROVED",
  "UC_REJECTED",
]);

export const hasFundBeenReleased = (status?: string) =>
  !!status && FUND_RELEASED_STATUSES.has(status);

/** The previous FA's utilization certificate, as `PreviousUcSummary` reads it. */
export type PreviousUcData = {
  id?: string;
  yearOfFundApplication?: number;
  status?: string;
  ecoRestorationCost?: number | string;
  totalReleasedTillDate?: number | string;
  releasedInThisFa?: number | string;
  utilizedAmount?: number | string | null;
  /** What this FA's own UC left unspent — the figure that carried onward. */
  unutilizedAmount?: number | string | null;
  /** What rolled *into* this FA from the one before it, where known. */
  carriedForward?: number | string | null;
  ucDate?: string | null;
  caName?: string | null;
  udinNumber?: string | null;
  financialProgress?: number | string;
  ucUrl?: string;
  revalidationUrl?: string;
};

/**
 * Pulls the previous FA's certificate out of the *current* FA's detail
 * response, which carries it alongside its own data.
 */
export const previousUcFromCurrentFa = (data: any): PreviousUcData | null => {
  const previous = data?.previousFaUc;

  if (!previous && !data?.previousUtilizationCertificateDocUrl) return null;

  return {
    id: previous?.id,
    yearOfFundApplication: previous?.yearOfFundApplication,
    status: previous?.status,
    ecoRestorationCost: data?.fundDetails?.landOwnership?.demandNote,
    totalReleasedTillDate: previous?.totalReleasedTillDate,
    releasedInThisFa: previous?.totalAmountReleased,
    utilizedAmount: previous?.utilizedAmount,
    // The same figure the current FA opens with — what that previous UC left
    // unspent. Its own opening balance is not carried in this response.
    unutilizedAmount:
      previous?.unutilizedAmount ?? data?.previousFaUnutilizedAmount,
    ucDate: previous?.ucDate,
    caName: previous?.caName,
    udinNumber: previous?.udinNumber,
    financialProgress: data?.totalFinancialProgress,
    ucUrl: data?.previousUtilizationCertificateDocUrl,
    revalidationUrl: data?.previousRevalidationLetterDocUrl,
  };
};

/**
 * Same shape, from a fund application's *own* detail response — used where the
 * prior FA is fetched directly (the new-FA form) rather than arriving with a
 * current FA. Its aggregates are already "as of" that application.
 */
export const previousUcFromFaDetail = (detail: any): PreviousUcData | null => {
  const fund = detail?.fundDetails;
  if (!fund) return null;

  return {
    id: fund.id,
    yearOfFundApplication: fund.yearOfFundApplication,
    status: fund.status,
    ecoRestorationCost: fund.landOwnership?.demandNote,
    totalReleasedTillDate: detail?.totalPaymentReleasedTillDate,
    releasedInThisFa: detail?.previousFaPaymentReleased,
    utilizedAmount: fund.utilizedAmount,
    unutilizedAmount: fund.unutilizedAmount,
    carriedForward: detail?.previousFaUnutilizedAmount,
    ucDate: fund.ucDate,
    caName: fund.caName,
    udinNumber: fund.udinNumber,
    financialProgress: detail?.totalFinancialProgress,
    ucUrl: detail?.utilizationCertificateDocUrl,
    revalidationUrl: detail?.revalidationLetterDocUrl,
  };
};

/** True when there is anything worth showing about the previous FA's UC. */
export const hasPreviousUcDetails = (uc?: PreviousUcData | null) =>
  !!uc &&
  (!!uc.ucUrl ||
    !!uc.revalidationUrl ||
    uc.utilizedAmount != null ||
    !!uc.ucDate);

/**
 * What the *previous* fund application certified.
 *
 * Rendered by `UtilizationCertificatePanel` itself, in view mode, so the
 * previous FA's certificate reads exactly like the Upload Utilization
 * Certificate panel it mirrors — same fields, same order, same two-column
 * layout — rather than a second arrangement of the same numbers that had to be
 * kept in step by hand. `PreviousUcData` is flat, so it is reshaped here into
 * the slice of the fund-application response that panel reads.
 */
export const PreviousUcSummary = ({ uc }: { uc: PreviousUcData }) => {
  const {
    id,
    yearOfFundApplication,
    status,
    utilizedAmount,
    unutilizedAmount,
    ucDate,
    caName,
    udinNumber,
    totalReleasedTillDate,
    releasedInThisFa,
    carriedForward,
    financialProgress,
    ucUrl,
    revalidationUrl,
  } = uc ?? {};

  // Keyed on the fields rather than on `uc`, which the pages rebuild on every
  // render — the panel's pre-fill effect watches `fundDetails` by identity, so
  // a fresh object each time would re-run it for nothing.
  const panelData = useMemo(
    () => ({
      fundDetails: {
        id,
        yearOfFundApplication,
        status,
        utilizedAmount,
        unutilizedAmount,
        ucDate,
        caName,
        udinNumber,
      },
      totalPaymentReleasedTillDate: totalReleasedTillDate,
      previousFaPaymentReleased: releasedInThisFa,
      previousFaUnutilizedAmount: carriedForward,
      totalFinancialProgress: financialProgress,
      utilizationCertificateDocUrl: ucUrl,
      revalidationLetterDocUrl: revalidationUrl,
    }),
    [
      id,
      yearOfFundApplication,
      status,
      utilizedAmount,
      unutilizedAmount,
      ucDate,
      caName,
      udinNumber,
      totalReleasedTillDate,
      releasedInThisFa,
      carriedForward,
      financialProgress,
      ucUrl,
      revalidationUrl,
    ]
  );

  return (
    <Box>
      {(id || status) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "18px",
          }}
        >
          <Box sx={{ fontSize: "13px", fontWeight: 600, color: fundUi.value }}>
            {[
              id,
              yearOfFundApplication
                ? `${ordinal(Number(yearOfFundApplication))} installment`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </Box>
          {status && <StatusPill status={status} />}
        </Box>
      )}

      <UtilizationCertificatePanel
        data={panelData}
        ecoRestorationCost={uc?.ecoRestorationCost}
        mode="view"
      />
    </Box>
  );
};

/**
 * Styles for the "Raise Query" action. The shared outlined button fades to
 * `opacity: 0.2` with no border when disabled, which all but hides it — a query
 * is a normal way out of these screens, so it stays legible until it is used.
 */
export const queryButtonStyles = {
  root: {
    fontSize: "16px",
  },
  outlined: {
    color: fundUi.iconGreen,
    borderColor: "#9FCFC4",
    fontWeight: 600,
    "&:hover": {
      borderColor: fundUi.green,
      backgroundColor: fundUi.softGreen,
    },
    "&:disabled": {
      opacity: 1,
      border: "1px solid #C4D8D1",
      backgroundColor: "#F2F7F5",
      color: "#7B8A86",
    },
  },
};

/** Explains why an action is not available yet, under the button itself. */
export const ActionNote = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      fontSize: "11.5px",
      color: fundUi.label,
      lineHeight: 1.4,
      maxWidth: "260px",
    }}
  >
    {children}
  </Box>
);

export type ConfirmTone = "primary" | "danger";

export type PendingAction = {
  /** Dialog heading — name the action, e.g. "Release fund?". */
  title: string;
  /** What is about to happen, in one or two sentences. */
  message: ReactNode;
  /** Label of the confirming button; defaults to the panel's own wording. */
  confirmText: string;
  tone?: ConfirmTone;
  onConfirm: () => void;
};

/**
 * Confirmation step in front of every fund action. These are one-way — a
 * released fund or a rejected application cannot be taken back from the UI —
 * so each one is spelled out before it runs.
 */
export const ConfirmActionDialog = ({
  action,
  onClose,
  isProcessing = false,
}: {
  action: PendingAction | null;
  /**
   * Closes the dialog. Called on cancel, and by the page once the confirmed
   * action has settled — not the moment it is fired.
   */
  onClose: () => void;
  /**
   * True while the confirmed action is still in flight. The dialog holds its
   * place with the confirm button spinning rather than vanishing the instant
   * the mutation is fired, which is what made these actions look like they had
   * done nothing at all.
   */
  isProcessing?: boolean;
}) => {
  const tone = action?.tone === "danger" ? TONE_COLORS.danger : null;

  return (
    <PopUp
      open={!!action}
      // Clicking away mid-action would strand the user with no sign the action
      // is still running, so the dialog only closes once it has settled.
      handleClose={isProcessing ? () => {} : onClose}
      heading=""
      actionLeft={{ hidden: true }}
      actionRight={{ hidden: true }}
    >
      <Box sx={{ maxWidth: "440px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          <Box
            sx={{
              width: "40px",
              height: "40px",
              flexShrink: 0,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: tone ? tone.bg : fundUi.softGreen,
              color: tone ? tone.color : fundUi.iconGreen,
              "& svg": { fontSize: "22px" },
            }}
          >
            <HelpOutlineRoundedIcon />
          </Box>
          <Box
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: fundUi.value,
              lineHeight: 1.3,
            }}
          >
            {action?.title}
          </Box>
        </Box>
        <Box
          sx={{
            fontSize: "14px",
            color: fundUi.label,
            lineHeight: 1.6,
          }}
        >
          {action?.message}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "14px",
            marginTop: "28px",
          }}
        >
          <Button
            text="Cancel"
            variant="outlined"
            onClick={onClose}
            disabled={isProcessing}
          />
          <Button
            text={action?.confirmText ?? "Confirm"}
            loading={isProcessing}
            onClick={() => action?.onConfirm()}
            customStyles={
              tone
                ? {
                    root: {
                      backgroundColor: tone.color,
                      "&:hover": { backgroundColor: tone.color, opacity: 0.9 },
                    },
                  }
                : undefined
            }
          />
        </Box>
      </Box>
    </PopUp>
  );
};

/**
 * Thin bar pinned to the top of the window while the page is refetching in the
 * background. A status change lands by refetching in place, which otherwise
 * swapped the content with no sign anything had happened.
 */
export const PageProgressBar = ({ show }: { show: boolean }) =>
  show ? (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1500,
        "@media print": { display: "none" },
      }}
    >
      <LinearProgress
        sx={{
          height: "3px",
          backgroundColor: fundUi.softGreen,
          "& .MuiLinearProgress-bar": { backgroundColor: fundUi.green },
        }}
      />
    </Box>
  ) : null;

/** One placeholder card, shaped like the `SectionCard` that replaces it. */
const SkeletonCard = ({
  rows = 4,
  fullWidth = false,
  timeline = false,
}: {
  rows?: number;
  fullWidth?: boolean;
  /** The wide milestone strip at the top, rather than a list of fields. */
  timeline?: boolean;
}) => (
  <Box
    sx={{
      ...(cardBase as object),
      ...(fullWidth ? { gridColumn: "1 / -1" } : {}),
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "18px",
      }}
    >
      <Skeleton
        variant="rounded"
        width={40}
        height={40}
        sx={{ borderRadius: "11px", flexShrink: 0 }}
      />
      <Skeleton variant="text" width="55%" height={18} />
    </Box>
    {timeline ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Fragment key={index}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexShrink: 0,
              }}
            >
              <Skeleton variant="circular" width={24} height={24} />
              <Box>
                <Skeleton variant="text" width={92} height={14} />
                <Skeleton variant="text" width={58} height={12} />
              </Box>
            </Box>
            {index < 5 && (
              <Skeleton
                variant="text"
                sx={{ flex: "1 1 24px", minWidth: "20px" }}
              />
            )}
          </Fragment>
        ))}
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index}>
            <Skeleton variant="text" width="45%" height={13} />
            <Skeleton variant="text" width="72%" height={17} />
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/**
 * Stand-in for the whole detail page while it loads. It mirrors the real
 * layout — heading, milestone strip, then the grid of detail cards — so the
 * content settles into a shape already on screen rather than replacing a
 * centred spinner, which made the wait read as longer than it is.
 */
export const FundDetailSkeleton = () => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <Skeleton variant="text" width={270} height={30} />
      <Skeleton
        variant="rounded"
        width={116}
        height={28}
        sx={{ borderRadius: "999px" }}
      />
    </Box>
    <Skeleton
      variant="text"
      width={160}
      height={18}
      sx={{ marginTop: "4px" }}
    />

    <DetailLayout>
      <SkeletonCard fullWidth timeline />
      <SkeletonCard rows={5} />
      <SkeletonCard rows={6} />
      <SkeletonCard rows={6} />
      <SkeletonCard rows={2} />
      <SkeletonCard rows={2} />
    </DetailLayout>
  </Box>
);

/**
 * Shown when the fetch settles with nothing to render — a bad id, a removed
 * application, or a failed request. That case used to sit on the loading
 * spinner indefinitely.
 */
export const FundLoadError = ({ onRetry }: { onRetry?: () => void }) => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "14px",
      textAlign: "center",
      padding: "24px",
    }}
  >
    <Box
      sx={{
        width: "48px",
        height: "48px",
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: TONE_COLORS.danger.bg,
        color: TONE_COLORS.danger.color,
        "& svg": { fontSize: "26px" },
      }}
    >
      <PriorityHighRoundedIcon />
    </Box>
    <Box sx={{ fontSize: "16px", fontWeight: 600, color: fundUi.value }}>
      This fund application could not be loaded
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        color: fundUi.label,
        maxWidth: "380px",
        lineHeight: 1.6,
      }}
    >
      It may have been removed, or the link may be incorrect. Check the link and
      try again.
    </Box>
    {onRetry && (
      <Button text="Try Again" variant="outlined" onClick={onRetry} />
    )}
  </Box>
);

/**
 * The backend's own message where it sent one, so a failed action says what
 * actually failed instead of a generic line.
 */
export const readApiError = (error: any, fallback: string) => {
  const data = error?.response?.data;

  // The standard error envelope names the offending fields in `errors`, which
  // is the whole of what a bare "Data validation error" message leaves out.
  // Manual throws put an arbitrary object there instead, so only the array
  // form is read.
  const fields = Array.isArray(data?.errors)
    ? data.errors
        .map((item: any) => item?.error || item?.field)
        .filter(Boolean)
        .join(", ")
    : "";

  if (data?.message && fields) return `${data.message}: ${fields}`;

  return data?.message || fallback;
};
